import { fetchAPI, METHODS, parseBody } from './common';
import qs from 'qs';

/**
 * Dashboard API client for the Distributions / Home Delivery feature.
 *
 * Mirrors src/backend/donations.js conventions:
 * - GET list endpoints return the parsed `.results` array.
 * - Detail / create / update / action endpoints return the parsed entity.
 * - Updates use METHODS.put (the backend has no PATCH).
 * - List query params are serialized with qs.stringify.
 */

const buildQuery = (params) => {
  const qStr = qs.stringify(params ?? {});
  return qStr ? `?${qStr}` : '';
};

/* -------------------------------------------------------------------------- */
/*                                  Programs                                   */
/* -------------------------------------------------------------------------- */

/**
 * List distribution programs for the team.
 * @returns {Promise<Object[]>}
 */
export async function getPrograms() {
  const res = await fetchAPI('/distribution/programs', METHODS.get);
  const { results } = await parseBody(res);
  return results;
}

/**
 * Get a single program (with enrollments).
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function getProgram(id) {
  const res = await fetchAPI(`/distribution/programs/${id}`, METHODS.get);
  return parseBody(res);
}

/**
 * Create a program.
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function createProgram(data) {
  const res = await fetchAPI('/distribution/programs', METHODS.post, data);
  return parseBody(res);
}

/**
 * Update a program.
 * @param {string} id
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function updateProgram(id, data) {
  const res = await fetchAPI(`/distribution/programs/${id}`, METHODS.put, data);
  return parseBody(res);
}

/* -------------------------------------------------------------------------- */
/*                                Pickup sites                                 */
/* -------------------------------------------------------------------------- */

/**
 * List pickup sites for the team.
 * @returns {Promise<Object[]>}
 */
export async function getPickupSites() {
  const res = await fetchAPI('/distribution/pickup-sites', METHODS.get);
  const { results } = await parseBody(res);
  return results;
}

/**
 * Create a pickup site.
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function createPickupSite(data) {
  const res = await fetchAPI('/distribution/pickup-sites', METHODS.post, data);
  return parseBody(res);
}

/**
 * Update a pickup site.
 * @param {string} id
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function updatePickupSite(id, data) {
  const res = await fetchAPI(
    `/distribution/pickup-sites/${id}`,
    METHODS.put,
    data
  );
  return parseBody(res);
}

/* -------------------------------------------------------------------------- */
/*                                 Households                                  */
/* -------------------------------------------------------------------------- */

/**
 * List households for the team.
 * @param {Object} [params] - { status, search }
 * @returns {Promise<Object[]>}
 */
export async function getHouseholds(params) {
  const res = await fetchAPI(
    `/distribution/households${buildQuery(params)}`,
    METHODS.get
  );
  const { results } = await parseBody(res);
  return results;
}

/**
 * Get a single household (with contacts, enrollments, schedules, recent stops).
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function getHousehold(id) {
  const res = await fetchAPI(`/distribution/households/${id}`, METHODS.get);
  return parseBody(res);
}

/**
 * Create a household.
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function createHousehold(data) {
  const res = await fetchAPI('/distribution/households', METHODS.post, data);
  return parseBody(res);
}

/**
 * Update a household.
 * @param {string} id
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function updateHousehold(id, data) {
  const res = await fetchAPI(
    `/distribution/households/${id}`,
    METHODS.put,
    data
  );
  return parseBody(res);
}

/**
 * List reusable individual (Freshcue) members for the team.
 * @returns {Promise<Object[]>}
 */
export async function getMembers() {
  const res = await fetchAPI('/distribution/members', METHODS.get);
  const { results } = await parseBody(res);
  return results;
}

/**
 * List the team's drivers (assignable to runs/stops). With `date`
 * (YYYY-MM-DD) each driver also has `availability` ({start_time, end_time}
 * in minutes, or null), `has_availability_set` and `routes_that_day`
 * (not counting `excludeRunId`).
 * @param {{date?: string, excludeRunId?: string}} [params]
 * @returns {Promise<Object[]>}
 */
export async function getDrivers({ date, excludeRunId } = {}) {
  const res = await fetchAPI(
    `/distribution/drivers${buildQuery({
      date: date || undefined,
      excludeRunId: excludeRunId || undefined,
    })}`,
    METHODS.get
  );
  const { results } = await parseBody(res);
  return results;
}

/* -------------------------------------------------------------------------- */
/*                            Item catalog (org-wide)                          */
/* -------------------------------------------------------------------------- */

export async function getItems() {
  const res = await fetchAPI('/distribution/items', METHODS.get);
  const { results } = await parseBody(res);
  return results;
}

export async function createItem(data) {
  const res = await fetchAPI('/distribution/items', METHODS.post, data);
  return parseBody(res);
}

export async function updateItem(id, data) {
  const res = await fetchAPI(`/distribution/items/${id}`, METHODS.put, data);
  return parseBody(res);
}

export async function deleteItem(id) {
  const res = await fetchAPI(`/distribution/items/${id}`, METHODS.delete);
  return parseBody(res);
}

/**
 * Manually link (or, with nulls, unlink) a member login account to a household.
 * @param {string} householdId
 * @param {{teamUserId: string|null, userId: string|null}} link
 */
export async function linkHouseholdAccount(
  householdId,
  { teamUserId, userId }
) {
  // The backend reads the linked member's account id as `linkedUserId` so it
  // never collides with the acting user injected as `userId`.
  return updateHousehold(householdId, { teamUserId, linkedUserId: userId });
}

/* -------------------------------------------------------------------------- */
/*                                  Contacts                                   */
/* -------------------------------------------------------------------------- */

/**
 * Create a contact on a household.
 * @param {string} householdId
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function createContact(householdId, data) {
  const res = await fetchAPI(
    `/distribution/households/${householdId}/contacts`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

/**
 * Update a contact.
 * @param {string} id
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function updateContact(id, data) {
  const res = await fetchAPI(`/distribution/contacts/${id}`, METHODS.put, data);
  return parseBody(res);
}

/**
 * Delete a contact.
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function deleteContact(id) {
  const res = await fetchAPI(`/distribution/contacts/${id}`, METHODS.delete);
  return parseBody(res);
}

/* -------------------------------------------------------------------------- */
/*                                Enrollments                                  */
/* -------------------------------------------------------------------------- */

/**
 * Create an enrollment.
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function createEnrollment(data) {
  const res = await fetchAPI('/distribution/enrollments', METHODS.post, data);
  return parseBody(res);
}

/**
 * Update an enrollment.
 * @param {string} id
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function updateEnrollment(id, data) {
  const res = await fetchAPI(
    `/distribution/enrollments/${id}`,
    METHODS.put,
    data
  );
  return parseBody(res);
}

/* -------------------------------------------------------------------------- */
/*                                 Schedules                                   */
/* -------------------------------------------------------------------------- */

/**
 * List schedules.
 * @param {Object} [params] - { programId, householdId, status }
 * @returns {Promise<Object[]>}
 */
export async function getSchedules(params) {
  const res = await fetchAPI(
    `/distribution/schedules${buildQuery(params)}`,
    METHODS.get
  );
  const { results } = await parseBody(res);
  return results;
}

/**
 * Create a schedule.
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function createSchedule(data) {
  const res = await fetchAPI('/distribution/schedules', METHODS.post, data);
  return parseBody(res);
}

/**
 * Update a schedule.
 * @param {string} id
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function updateSchedule(id, data) {
  const res = await fetchAPI(
    `/distribution/schedules/${id}`,
    METHODS.put,
    data
  );
  return parseBody(res);
}

/* -------------------------------------------------------------------------- */
/*                                   Stops                                     */
/* -------------------------------------------------------------------------- */

/**
 * List stops.
 * @param {Object} [params] - { status, programId, scheduledDate, driverUserId, runId }
 * @returns {Promise<Object[]>}
 */
export async function getStops(params) {
  const res = await fetchAPI(
    `/distribution/stops${buildQuery(params)}`,
    METHODS.get
  );
  const { results } = await parseBody(res);
  return results;
}

/**
 * Get a single stop (with items and events).
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function getStop(id) {
  const res = await fetchAPI(`/distribution/stops/${id}`, METHODS.get);
  return parseBody(res);
}

/**
 * Create a stop (request intake).
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function createStop(data) {
  const res = await fetchAPI('/distribution/stops', METHODS.post, data);
  return parseBody(res);
}

/**
 * Update a stop (including items).
 * @param {string} id
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function updateStop(id, data) {
  const res = await fetchAPI(`/distribution/stops/${id}`, METHODS.put, data);
  return parseBody(res);
}

/**
 * Assign a stop to a driver.
 * @param {string} id
 * @param {Object} data - { driverUserId }
 * @returns {Promise<Object>}
 */
export async function assignStop(id, data) {
  const res = await fetchAPI(
    `/distribution/stops/${id}/assign`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

/**
 * Post a stop (make it available to claim).
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function postStop(id) {
  const res = await fetchAPI(`/distribution/stops/${id}/post`, METHODS.post);
  return parseBody(res);
}

/**
 * Complete a stop.
 * @param {string} id
 * @param {Object} data - { proofPhotoUrl?, signatureUrl?, verificationCode?, completionNotes? }
 * @returns {Promise<Object>}
 */
export async function completeStop(id, data) {
  const res = await fetchAPI(
    `/distribution/stops/${id}/complete`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

/**
 * Mark a stop as missed.
 * @param {string} id
 * @param {Object} data - { missedReason, needsFollowUp? }
 * @returns {Promise<Object>}
 */
export async function missStop(id, data) {
  const res = await fetchAPI(
    `/distribution/stops/${id}/miss`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

/* -------------------------------------------------------------------------- */
/*                                    Runs                                     */
/* -------------------------------------------------------------------------- */

/**
 * List runs.
 * @param {Object} [params] - { serviceDate, status, driverUserId }
 * @returns {Promise<Object[]>}
 */
export async function getRuns(params) {
  const res = await fetchAPI(
    `/distribution/runs${buildQuery(params)}`,
    METHODS.get
  );
  const { results } = await parseBody(res);
  return results;
}

/**
 * Get a single run (with trips, manifests, stops).
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function getRun(id) {
  const res = await fetchAPI(`/distribution/runs/${id}`, METHODS.get);
  return parseBody(res);
}

/**
 * Create a run (optionally auto-build trips from stopIds).
 * @param {Object} data - { serviceDate, name, assignedDriverUserId?, stopIds?[] }
 * @returns {Promise<Object>}
 */
export async function createRun(data) {
  const res = await fetchAPI('/distribution/runs', METHODS.post, data);
  return parseBody(res);
}

/**
 * Update a run.
 * @param {string} id
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function updateRun(id, data) {
  const res = await fetchAPI(`/distribution/runs/${id}`, METHODS.put, data);
  return parseBody(res);
}

/**
 * Build a run from a set of stops (auto-group into trips).
 * @param {string} id
 * @param {Object} data - { stopIds[] }
 * @returns {Promise<Object>}
 */
export async function buildRun(id, data) {
  const res = await fetchAPI(
    `/distribution/runs/${id}/build`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

/**
 * Suggest a road-aware stop order for one route trip. Persists nothing: the
 * caller saves the order through buildRun.
 * @param {string} id run id
 * @param {{ tripId: string, stopIds: string[] }} data current local order
 * @returns {Promise<{ stopIds: string[], method: string, fallback: boolean,
 *   reason?: string, legDurationsS?: number[] }>}
 */
export async function optimizeRunOrder(id, data) {
  const res = await fetchAPI(
    `/distribution/runs/${id}/optimize-order`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

/**
 * Publish a run to the volunteer claim board: opens its not-yet-started stops
 * for claiming and moves the run from draft to published.
 * @param {string} id run id
 * @returns {Promise<Object>} refreshed run detail
 */
export async function publishRun(id) {
  const res = await fetchAPI(`/distribution/runs/${id}/publish`, METHODS.post);
  return parseBody(res);
}

/**
 * Cancel a route. Pre-pickup only.
 * @param {string} id
 * @param {'release'|'cancel'} stopAction - what happens to its deliveries
 * @param {string} [reason]
 */
export async function cancelRun(id, stopAction, reason) {
  const res = await fetchAPI(`/distribution/runs/${id}/cancel`, METHODS.post, {
    stopAction,
    reason: reason || null,
  });
  return parseBody(res);
}

/**
 * Confirm pickup for a trip on a run (cascades child stops).
 * @param {string} runId
 * @param {string} tripId
 * @returns {Promise<Object>}
 */
export async function tripPickup(runId, tripId) {
  const res = await fetchAPI(
    `/distribution/runs/${runId}/trips/${tripId}/pickup`,
    METHODS.post
  );
  return parseBody(res);
}

/* -------------------------------------------------------------------------- */
/*                              Driver / volunteer                             */
/* -------------------------------------------------------------------------- */

export async function getDriverAssignments(params) {
  const res = await fetchAPI(
    `/distribution/driver/assignments/today${buildQuery(params)}`,
    METHODS.get
  );
  return parseBody(res);
}

export async function getDriverAvailable(params) {
  const res = await fetchAPI(
    `/distribution/driver/available${buildQuery(params)}`,
    METHODS.get
  );
  return parseBody(res);
}

export async function getDriverRun(id) {
  const res = await fetchAPI(`/distribution/driver/runs/${id}`, METHODS.get);
  return parseBody(res);
}

export async function claimDriverStop(id) {
  const res = await fetchAPI(
    `/distribution/driver/stops/${id}/claim`,
    METHODS.post
  );
  return parseBody(res);
}

export async function claimDriverTrip(id) {
  const res = await fetchAPI(
    `/distribution/driver/trips/${id}/claim`,
    METHODS.post
  );
  return parseBody(res);
}

/**
 * Preview a personal route before claiming: ordered stops, path, leg times.
 * @param {Object} data - { stopIds, stopOrder?, origin?: {lat,lng} }
 */
export async function previewDriverRoute(data) {
  const res = await fetchAPI(
    '/distribution/driver/routes/preview',
    METHODS.post,
    data
  );
  return parseBody(res);
}

/**
 * Claim multiple standalone deliveries and assemble a personal route.
 * @param {Object} data - { stopIds, pickupSiteId?, orderMode?: 'auto'|'manual', stopOrder?, origin?, name? }
 */
export async function claimAndBuildRoute(data) {
  const res = await fetchAPI(
    '/distribution/driver/routes/claim-and-build',
    METHODS.post,
    data
  );
  return parseBody(res);
}

/**
 * Reorder stops on a claimed trip (before pickup).
 * @param {string} tripId
 * @param {Object} data - { stopIds: string[] }
 */
export async function reorderDriverTripStops(tripId, data) {
  const res = await fetchAPI(
    `/distribution/driver/trips/${tripId}/reorder`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

/**
 * Append open deliveries onto a claimed trip (pre-pickup).
 * @param {string} tripId
 * @param {Object} data - { stopIds, orderMode?, stopOrder? }
 */
export async function appendDriverTripStops(tripId, data) {
  const res = await fetchAPI(
    `/distribution/driver/trips/${tripId}/append-stops`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

/**
 * Remove a driver-added (or personal-build) stop from a trip before pickup.
 * @param {string} tripId
 * @param {string} stopId
 * @param {Object} [data]
 */
export async function removeDriverTripStop(tripId, stopId, data = {}) {
  const res = await fetchAPI(
    `/distribution/driver/trips/${tripId}/stops/${stopId}/remove`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

export async function releaseDriverStop(id, data) {
  const res = await fetchAPI(
    `/distribution/driver/stops/${id}/release`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

export async function releaseDriverTrip(id, data) {
  const res = await fetchAPI(
    `/distribution/driver/trips/${id}/release`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

export async function driverTripPickup(tripId) {
  const res = await fetchAPI(
    `/distribution/driver/trips/${tripId}/pickup`,
    METHODS.post
  );
  return parseBody(res);
}

export async function updateDriverStopStatus(id, data) {
  const res = await fetchAPI(
    `/distribution/driver/stops/${id}/status`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

/* -------------------------------------------------------------------------- */
/*                                  Reports                                    */
/* -------------------------------------------------------------------------- */

/**
 * Get the distributions summary report.
 * @param {Object} [params] - { startDate, endDate }
 * @returns {Promise<Object>}
 */
export async function getSummary(params) {
  const res = await fetchAPI(
    `/distribution/reports/summary${buildQuery(params)}`,
    METHODS.get
  );
  return parseBody(res);
}

/* -------------------------------------------------------------------------- */
/*                               Pilot ops                                     */
/* -------------------------------------------------------------------------- */

/**
 * Day-of diagnostics for staff (empty board reasons, geocode gaps, drafts).
 * @param {Object} [params] - { date: 'YYYY-MM-DD' }
 */
export async function getDayOpsStatus(params) {
  const res = await fetchAPI(
    `/distribution/ops/day-status${buildQuery(params)}`,
    METHODS.get
  );
  return parseBody(res);
}

/** Setup milestones. */
export async function getSetupStatus() {
  const res = await fetchAPI('/distribution/ops/setup-status', METHODS.get);
  return parseBody(res);
}

/**
 * Materialize recurring schedules into stops for a calendar date.
 * @param {Object} data - { date: 'YYYY-MM-DD', daysAhead?: number }
 */
export async function generateStopsForDate(data) {
  const res = await fetchAPI(
    '/distribution/ops/generate-stops',
    METHODS.post,
    data
  );
  return parseBody(res);
}

/**
 * Publish all draft routes for a service date.
 * @param {Object} data - { date: 'YYYY-MM-DD' }
 */
export async function publishDraftRunsForDate(data) {
  const res = await fetchAPI(
    '/distribution/ops/publish-drafts',
    METHODS.post,
    data
  );
  return parseBody(res);
}

/** Bulk cancel open stops for a service day. */
export async function cancelServiceDay(data) {
  const res = await fetchAPI(
    '/distribution/ops/cancel-day',
    METHODS.post,
    data
  );
  return parseBody(res);
}

/** @deprecated Prefer api.team.listServiceCalendar — org service calendar. */
export async function listServiceExceptions(params) {
  const res = await fetchAPI(
    `/team/service-calendar${buildQuery(params)}`,
    METHODS.get
  );
  return parseBody(res);
}

/** @deprecated Prefer api.team.upsertServiceCalendarDay */
export async function upsertServiceException(data) {
  const res = await fetchAPI('/team/service-calendar', METHODS.post, data);
  return parseBody(res);
}

/** @deprecated Prefer api.team.deleteServiceCalendarDay */
export async function deleteServiceException(date) {
  const res = await fetchAPI(
    `/team/service-calendar/${encodeURIComponent(date)}`,
    METHODS.delete
  );
  return parseBody(res);
}

export async function getGeocodeAudit() {
  const res = await fetchAPI('/distribution/ops/geocode-audit', METHODS.get);
  return parseBody(res);
}

/* -------------------------------------------------------------------------- */
/*                                   Clients                                   */
/* -------------------------------------------------------------------------- */

/**
 * List clients (people served) for the team.
 * @param {Object} [params] - { search, householdId, status }
 * @returns {Promise<Object[]>}
 */
export async function getClients(params) {
  const res = await fetchAPI(
    `/distribution/clients${buildQuery(params)}`,
    METHODS.get
  );
  const { results } = await parseBody(res);
  return results;
}

/**
 * Get a client with location, enrollments (+ schedules), contacts, recent stops.
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function getClient(id) {
  const res = await fetchAPI(`/distribution/clients/${id}`, METHODS.get);
  return parseBody(res);
}

/**
 * Create a client at an existing location (`householdId`) or a new one
 * (`location`).
 * @param {Object} data
 * @returns {Promise<{id: string, householdId: string}>}
 */
export async function createClient(data) {
  const res = await fetchAPI('/distribution/clients', METHODS.post, data);
  return parseBody(res);
}

/**
 * Update a client (pass `householdId` to move them).
 * @param {string} id
 * @param {Object} data
 * @returns {Promise<Object>} includes futureStopSync
 */
export async function updateClient(id, data) {
  const res = await fetchAPI(`/distribution/clients/${id}`, METHODS.put, data);
  return parseBody(res);
}

/**
 * Pause a client across programs (`until`: 'YYYY-MM-DD' | 'indefinite') or
 * resume (`until`: null).
 * @param {string} id
 * @param {{until: string|null, reason?: string|null}} data
 * @returns {Promise<Object>} includes futureStopSync
 */
export async function setClientHold(id, data) {
  const res = await fetchAPI(
    `/distribution/clients/${id}/hold`,
    METHODS.put,
    data
  );
  return parseBody(res);
}

/**
 * Delete a client (deactivated instead when they have delivery history).
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function deleteClient(id) {
  const res = await fetchAPI(`/distribution/clients/${id}`, METHODS.delete);
  return parseBody(res);
}

/**
 * Which existing delivery location (and clients) an address would join.
 * @param {{placeId?: string, formattedAddress?: string, suiteNumber?: string}} params
 * @returns {Promise<{location: Object|null}>}
 */
export async function matchClientLocation(params) {
  const res = await fetchAPI(
    `/distribution/clients/location-match${buildQuery(params)}`,
    METHODS.get
  );
  return parseBody(res);
}

/**
 * Hub prep counts for a service day (per route + day totals by program/item).
 * @param {{date: string, pickupSiteId?: string}} params
 * @returns {Promise<Object>}
 */
export async function getHubPrepList(params) {
  const res = await fetchAPI(
    `/distribution/ops/prep-list${buildQuery(params)}`,
    METHODS.get
  );
  return parseBody(res);
}

/**
 * Routes from past days that are still open (draft/published/in progress).
 * @returns {Promise<Object[]>}
 */
export async function getRunsNeedingCloseOut() {
  const res = await fetchAPI('/distribution/runs/needs-close-out', METHODS.get);
  const { results } = await parseBody(res);
  return results;
}

/**
 * Close out a past-day route: open deliveries become missed (follow-ups) or
 * canceled; delivered ones are finalized; the route is completed.
 * @param {string} id run id
 * @param {{outcome: 'missed'|'canceled', reason?: string}} data
 */
export async function closeOutRun(id, data) {
  const res = await fetchAPI(
    `/distribution/runs/${id}/close-out`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

/**
 * Close a missed delivery's follow-up; the stop stays counted as missed.
 * @param {string} id stop id
 * @param {{notes?: string}} [data]
 */
export async function resolveFollowUp(id, data = {}) {
  const res = await fetchAPI(
    `/distribution/stops/${id}/resolve-follow-up`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

/**
 * Reschedule a missed delivery: closes its follow-up and creates a new
 * unrouted stop on the new date with the same clients and items.
 * @param {string} id stop id
 * @param {{scheduledDate: string, windowStartAt?: string, windowEndAt?: string, notes?: string}} data
 * @returns {Promise<{id: string, rescheduledFromStopId: string}>}
 */
export async function rescheduleFollowUp(id, data) {
  const res = await fetchAPI(
    `/distribution/stops/${id}/reschedule`,
    METHODS.post,
    data
  );
  return parseBody(res);
}

/* -------------------------------------------------------------------------- */
/*                              Standing routes                                */
/* -------------------------------------------------------------------------- */

/**
 * List standing routes (weekly routes built automatically each day).
 * @param {{active?: boolean}} [params]
 * @returns {Promise<Object[]>}
 */
export async function getStandingRoutes(params) {
  const res = await fetchAPI(
    `/distribution/standing-routes${buildQuery(params)}`,
    METHODS.get
  );
  const { results } = await parseBody(res);
  return results;
}

/**
 * A standing route with its ordered members.
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function getStandingRoute(id) {
  const res = await fetchAPI(
    `/distribution/standing-routes/${id}`,
    METHODS.get
  );
  return parseBody(res);
}

/**
 * Active client program enrollments that can be added to a standing route.
 * @returns {Promise<Object[]>}
 */
export async function getStandingRouteEnrollmentOptions() {
  const res = await fetchAPI(
    '/distribution/standing-routes/enrollment-options',
    METHODS.get
  );
  const { results } = await parseBody(res);
  return results;
}

/**
 * Create a standing route.
 * @param {{name: string, weekdays: number[], pickupSiteId: string, pickupWindowStart: string, pickupWindowEnd: string, driverUserId?: string|null, enrollmentIds?: string[]}} data
 * @returns {Promise<Object>}
 */
export async function createStandingRoute(data) {
  const res = await fetchAPI(
    '/distribution/standing-routes',
    METHODS.post,
    data
  );
  return parseBody(res);
}

/**
 * Update a standing route (partial; enrollmentIds replaces the member list).
 * @param {string} id
 * @param {Object} data
 * @returns {Promise<Object>}
 */
export async function updateStandingRoute(id, data) {
  const res = await fetchAPI(
    `/distribution/standing-routes/${id}`,
    METHODS.put,
    data
  );
  return parseBody(res);
}

/**
 * Archive a standing route; it stops building runs.
 * @param {string} id
 * @returns {Promise<Object>}
 */
export async function archiveStandingRoute(id) {
  const res = await fetchAPI(
    `/distribution/standing-routes/${id}/archive`,
    METHODS.post
  );
  return parseBody(res);
}

/**
 * What a standing route would pick up on a date.
 * @param {string} id
 * @param {string} date YYYY-MM-DD
 * @returns {Promise<Object>}
 */
export async function previewStandingRoute(id, date) {
  const res = await fetchAPI(
    `/distribution/standing-routes/${id}/preview${buildQuery({ date })}`,
    METHODS.get
  );
  return parseBody(res);
}

/**
 * Build every standing route's run for a date.
 * @param {{date: string}} data
 * @returns {Promise<{created: number, updated: number, unchanged: number, failed: number, needsDriver: number, results: Object[]}>}
 */
export async function buildStandingRoutesForDate(data) {
  const res = await fetchAPI(
    '/distribution/ops/build-standing-routes',
    METHODS.post,
    data
  );
  return parseBody(res);
}
